/**
 * Punto de composición: crea el mapa, las capas y conecta la interfaz.
 *
 * Es el único archivo que toca el DOM del panel. Todo lo demás recibe datos o
 * recibe el mapa, así que la lógica se puede testear y la lámina se puede
 * generar sin depender de cómo esté armado el panel.
 */

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-rotate';
import './styles.css';

import {
  DEFAULT_BASEMAP,
  DEFAULT_CENTER,
  DEFAULT_RADIUS_M,
  DEFAULT_ZOOM,
  GEOCODER_DEBOUNCE_MS,
} from './config.js';
import { autoDocCode, exportFileName, formatArea } from './domain/document.js';
import { coveragePercent } from './domain/coverage.js';
import { complianceGaps, isCompliant } from './domain/compliance.js';
import { relocationReaches100 } from './domain/relocation.js';
import { isInstalledMode } from './domain/inventory.js';
import { BasemapController } from './map/basemaps.js';
import { BuildingLayer } from './map/building-layer.js';
import { DeaLayer } from './map/dea-layer.js';
import { PerimeterLayer } from './map/perimeter-layer.js';
import { getBearing, rotationMapOptions, setBearing, supportsRotation } from './map/rotation.js';
import { loadAedImage } from './map/icons.js';
import { geocode, parseCoordinates, reverseGeocode } from './geocoding/geocoder.js';
import { Autosave } from './storage/autosave.js';
import {
  deleteProject,
  hasProject,
  listProjects,
  loadProject,
  saveProject,
} from './storage/library.js';
import { renderMapSource } from './export/render-map.js';
import { composeSheet } from './export/sheet.js';
import { downloadJson, downloadPdf, downloadPng } from './export/download.js';
import { renderSummary } from './ui/summary.js';
import { makeDraggable } from './ui/draggable.js';
import { openPreview } from './ui/preview.js';
import { frameContent, maxImageryZoom, waitForTiles } from './map/capture.js';
import logoUrl from './assets/logo-vitalheart.png';

const $ = (id) => document.getElementById(id);

// ---------------------------------------------------------------- Mapa y capas

const map = L.map('map', rotationMapOptions).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
const basemaps = new BasemapController(map);

const autosave = new Autosave(() => serializeProject());
const onChange = () => refresh();
const onCommit = () => {
  refresh();
  autosave.schedule();
};

const perimeter = new PerimeterLayer(map, { onChange, onCommit });
const deaLayer = new DeaLayer(map, { onChange, onCommit });
const buildingLayer = new BuildingLayer(map, { onChange, onCommit });

let radius = DEFAULT_RADIUS_M;
let logoImage = null;
let searchMarker = null;

// ------------------------------------------------------------- Estado derivado

/** Metadatos documentales tomados del panel. */
function metadata() {
  const sheetNumber = $('numLamina').value || 'L-01';
  const version = $('versionDoc').value || 'v1';
  const codeField = $('codigoDoc');
  return {
    sitio: $('sitio').value || '',
    sede: $('sede').value || '',
    direccion: $('direccion').value || '',
    numLamina: sheetNumber,
    versionDoc: version,
    codigoDoc: codeField.value || autoDocCode(sheetNumber, version),
  };
}

/**
 * El estado de la lámina como objeto plano. Es el único contrato entre la app y
 * todo lo que dibuja o evalúa: dominio, resumen y exportación consumen esto.
 */
function sheetState() {
  const deas = deaLayer.snapshot();
  const buildings = buildingLayer.snapshot();
  return {
    deas,
    buildings,
    perimeter: perimeter.points,
    areaM2: perimeter.areaM2,
    radius,
    coveragePct: coveragePercent(perimeter.points, deaLayer.centers(), radius),
    // ¿Reubicando lo que ya hay se llega al 100%? Cambia el consejo al cliente:
    // no es lo mismo pedirle que compre un equipo que pedirle que mueva el suyo.
    canRelocateHorizontal: relocationReaches100(perimeter.points, deas.length, radius),
    meta: metadata(),
    date: new Date(),
    aedImage: null,
  };
}

// ------------------------------------------------------------------ Renderizado

function refresh() {
  const sheet = sheetState();

  $('total').textContent = String(deaLayer.length);
  $('areaInfo').textContent = sheet.areaM2 > 0 ? `Superficie: ${formatArea(sheet.areaM2)}` : '';

  // El estado de cumplimiento se muestra en vivo, pero sólo cuando la lámina
  // documenta equipos instalados: una propuesta no cumple ni incumple nada.
  const complianceInfo = $('cumplimientoInfo');
  if (isInstalledMode(sheet.deas)) {
    const ok = isCompliant(sheet.coveragePct, sheet.buildings);
    const gaps = complianceGaps(sheet.coveragePct, sheet.buildings, {
      canRelocateHorizontal: sheet.canRelocateHorizontal,
    });
    complianceInfo.textContent = ok
      ? 'Art. 5º: CUMPLE'
      : `Art. 5º: NO CUMPLE · ${gaps.length} brecha${gaps.length === 1 ? '' : 's'}`;
    complianceInfo.classList.toggle('falla', !ok);
  } else {
    complianceInfo.textContent = '';
    complianceInfo.classList.remove('falla');
  }

  renderSummary($('resumen'), sheet);
  renderList();
}

function renderList() {
  const acquired = deaLayer.items.filter((dea) => dea.estado === 'adquirido').length;
  const proposed = deaLayer.length - acquired;
  const list = $('list');
  list.innerHTML = '';

  if (deaLayer.length) {
    const header = document.createElement('div');
    header.className = 'grupo';
    header.textContent = `Adquiridos ${acquired} · Propuestos ${proposed}`;
    list.appendChild(header);
  }

  deaLayer.items.forEach((dea) => {
    list.appendChild(
      listRow(
        dea.color,
        `DEA ${dea.num}`,
        `(${dea.estado})`,
        `Eliminar DEA ${dea.num}`,
        () => deaLayer.remove(dea.id),
      ),
    );
  });

  if (buildingLayer.length) {
    const header = document.createElement('div');
    header.className = 'grupo';
    header.textContent = `Edificios ${buildingLayer.length}`;
    list.appendChild(header);

    buildingLayer.items.forEach((building, index) => {
      list.appendChild(
        listRow(
          '#2f6db3',
          `${index + 1}. ${building.nombre}`,
          `(${building.pisos}p)`,
          `Eliminar ${building.nombre}`,
          () => buildingLayer.remove(building.num),
        ),
      );
    });
  }
}

/** Fila de la lista. Se construye por DOM, no por innerHTML: los nombres de
 *  edificio los escribe el operador y concatenarlos como HTML es una inyección. */
function listRow(color, label, state, removeLabel, onRemove) {
  const item = document.createElement('div');
  item.className = 'item';

  const left = document.createElement('span');
  const dot = document.createElement('span');
  dot.className = 'dot';
  dot.style.background = color;
  left.appendChild(dot);
  left.appendChild(document.createTextNode(`${label} `));
  const stateEl = document.createElement('span');
  stateEl.className = 'estado';
  stateEl.textContent = state;
  left.appendChild(stateEl);

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'del';
  remove.textContent = '×';
  remove.setAttribute('aria-label', removeLabel);
  remove.addEventListener('click', onRemove);

  item.append(left, remove);
  return item;
}

// ------------------------------------------------------------ Serialización

function serializeProject() {
  const center = map.getCenter();
  return {
    v: 1,
    ...metadata(),
    radio: radius,
    center: { lat: center.lat, lng: center.lng },
    zoom: map.getZoom(),
    bearing: getBearing(map),
    perimetro: perimeter.points,
    deas: deaLayer.snapshot(),
    edificios: buildingLayer.snapshot(),
    baseProv: basemaps.activeId,
  };
}

function loadProjectData(data) {
  if (!data) return;
  autosave.suspend();

  deaLayer.clear();
  buildingLayer.clear();
  perimeter.clear();

  $('sitio').value = data.sitio || '';
  $('sede').value = data.sede || '';
  $('direccion').value = data.direccion || '';
  $('numLamina').value = data.numLamina || 'L-01';
  $('versionDoc').value = data.versionDoc || 'v1';
  $('codigoDoc').value = data.codigoDoc || autoDocCode(data.numLamina, data.versionDoc);

  radius = data.radio || DEFAULT_RADIUS_M;
  $('radio').value = radius;
  deaLayer.setRadius(radius);

  if (data.center) {
    map.setView([data.center.lat, data.center.lng], data.zoom || DEFAULT_ZOOM, { animate: false });
  }
  if (data.bearing) {
    setBearing(map, data.bearing);
    $('bearing').value = data.bearing;
  }

  perimeter.load(data.perimetro);
  (data.deas || []).forEach((dea) =>
    deaLayer.add(dea.lat, dea.lng, dea.estado, dea.bearing || 0, dea.num, dea.icono),
  );
  (data.edificios || []).forEach((building) =>
    buildingLayer.add(building.lat, building.lng, building),
  );

  autosave.resume();
  refresh();
}

// ---------------------------------------------------------------- Exportación

/**
 * Prepara el mapa y rasteriza la lámina.
 *
 * `zoomDelta` deja al operador acercar o alejar desde la vista previa sin
 * perder el encuadre del contenido: se parte del encuadre calculado y se corre
 * el zoom desde ahí.
 */
async function buildSource(zoomDelta = 0) {
  const bounds = contentBounds();
  if (bounds) {
    frameContent(map, bounds, $('mapwrap'), basemaps.active);
    if (zoomDelta) {
      const target = map.getZoom() + zoomDelta;
      const ceiling = maxImageryZoom(basemaps.active);
      map.setZoom(Math.max(map.getMinZoom(), Math.min(ceiling, target)), { animate: false });
    }
    await waitForTiles(basemaps.active);
  }

  const aedImage = await loadAedImage();
  const source = renderMapSource({
    map,
    container: $('mapwrap'),
    baseLayer: basemaps.active,
    perimeter: perimeter.points,
    deas: deaLayer.items,
    buildings: buildingLayer.items,
    radius,
    aedImage,
  });
  source.aedImage = aedImage;
  return source;
}

/** Compone la lámina sobre un raster ya renderizado. Barato: se repite al arrastrar. */
function buildSheet(source, layout) {
  const sheet = { ...sheetState(), aedImage: source.aedImage };
  return composeSheet(source, sheet, {
    map,
    deaItems: deaLayer.items,
    buildingItems: buildingLayer.items,
    logoImage,
    layout,
  });
}

function currentFileName() {
  const meta = metadata();
  return exportFileName(meta.codigoDoc, meta.sitio, 'Cobertura_DEA');
}

/** ¿Hay algo que exportar? Una lámina vacía no es un entregable. */
function hasContent() {
  if (!deaLayer.length && !perimeter.isClosed) {
    window.alert('Agrega al menos un DEA o marca el perímetro.');
    return false;
  }
  return true;
}

/** Descarga directa, sin pasar por la vista previa. */
async function exportSheet(format) {
  if (!hasContent()) return;

  const button = format === 'pdf' ? $('btnPdf') : $('btnPng');
  const originalLabel = button.textContent;
  button.textContent = 'Preparando…';
  button.disabled = true;

  const previousCenter = map.getCenter();
  const previousZoom = map.getZoom();
  const previousBearing = getBearing(map);

  try {
    const source = await buildSource(0);
    const canvas = buildSheet(source, {});
    if (format === 'pdf') await downloadPdf(canvas, currentFileName(), metadata());
    else downloadPng(canvas, currentFileName());
  } catch (error) {
    window.alert(`No se pudo generar la lámina: ${error.message}`);
  } finally {
    map.setView(previousCenter, previousZoom, { animate: false });
    if (supportsRotation(map)) setBearing(map, previousBearing);
    button.textContent = originalLabel;
    button.disabled = false;
  }
}

/**
 * Abre la vista previa editable.
 *
 * Lo que se ve es el mismo canvas que se descarga: no hay una segunda ruta de
 * render que pueda divergir del entregable.
 */
function openSheetPreview() {
  if (!hasContent()) return;

  const previousCenter = map.getCenter();
  const previousZoom = map.getZoom();
  const previousBearing = getBearing(map);

  openPreview({
    renderSource: (zoomDelta) => buildSource(zoomDelta),
    compose: (source, layout) => buildSheet(source, layout),
    download: async (canvas, format) => {
      if (format === 'pdf') await downloadPdf(canvas, currentFileName(), metadata());
      else downloadPng(canvas, currentFileName());
    },
    onClose: () => {
      // Devolver la vista donde estaba: el operador no tiene por qué perder su
      // encuadre de trabajo por haber previsualizado.
      map.setView(previousCenter, previousZoom, { animate: false });
      if (supportsRotation(map)) setBearing(map, previousBearing);
    },
  });
}

/** Límites de todo lo dibujado, con los círculos completos. */
function contentBounds() {
  const points = [];
  deaLayer.items.forEach((dea) => {
    const bounds = dea.circle.getBounds();
    points.push(bounds.getNorthWest(), bounds.getSouthEast());
    if (dea.icono) points.push(dea.icono.getLatLng());
  });
  perimeter.points.forEach((point) => points.push(L.latLng(point[0], point[1])));
  buildingLayer.items.forEach((building) => {
    points.push(building.marker.getLatLng());
    if (building.icono) points.push(building.icono.getLatLng());
  });
  return points.length ? L.latLngBounds(points) : null;
}

// ------------------------------------------------------------------- Buscador

let searchTimer = null;
let searchAbort = null;

function closeSuggestions() {
  const box = $('sugerencias');
  box.style.display = 'none';
  box.innerHTML = '';
}

function showSuggestionMessage(text) {
  const box = $('sugerencias');
  box.innerHTML = '';
  const message = document.createElement('div');
  message.className = 'g';
  message.textContent = text;
  box.appendChild(message);
  box.style.display = 'block';
}

async function searchSuggestions(query) {
  showSuggestionMessage('Buscando…');
  searchAbort?.abort();
  searchAbort = new AbortController();

  let results;
  try {
    results = await geocode(query, map.getCenter(), searchAbort.signal);
  } catch (error) {
    if (error.name === 'AbortError') return;
    results = null;
  }

  if (results === null) {
    showSuggestionMessage('Sin conexión al buscador. Pega las coordenadas.');
    return;
  }
  if (!results.length) {
    showSuggestionMessage('Sin resultados. Prueba calle + comuna.');
    return;
  }

  const box = $('sugerencias');
  box.innerHTML = '';
  results.forEach((result) => {
    const option = document.createElement('div');
    option.setAttribute('role', 'option');
    option.textContent = result.title;
    if (result.subtitle) {
      const sub = document.createElement('div');
      sub.className = 'g';
      sub.textContent = result.subtitle;
      option.appendChild(sub);
    }
    option.addEventListener('click', () => {
      closeSuggestions();
      placeSearchMarker(result.lat, result.lon);
      setAddress(result.title + (result.subtitle ? `, ${result.subtitle}` : ''));
    });
    box.appendChild(option);
  });
  box.style.display = 'block';
}

function setAddress(text) {
  if (!text) return;
  $('direccion').value = text;
  onCommit();
}

function placeSearchMarker(lat, lon, zoom = 17) {
  if (searchMarker) map.removeLayer(searchMarker);
  searchMarker = L.marker([lat, lon], { draggable: true }).addTo(map);
  searchMarker.bindTooltip('Punto (arrástrame para ajustar)', { direction: 'top' });
  searchMarker.on('dragend', async () => {
    const position = searchMarker.getLatLng();
    const address = await reverseGeocode(position.lat, position.lng);
    setAddress(address || `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`);
  });
  setBearing(map, 0);
  $('bearing').value = 0;
  map.setView([lat, lon], zoom);
}

async function goToPlace() {
  const query = $('goto').value.trim();
  if (!query) return;
  closeSuggestions();

  const coordinates = parseCoordinates(query);
  if (coordinates) {
    placeSearchMarker(coordinates[0], coordinates[1]);
    const address = await reverseGeocode(coordinates[0], coordinates[1]);
    setAddress(address);
    return;
  }

  const results = await geocode(query, map.getCenter());
  if (results === null) {
    window.alert('No se pudo conectar al buscador. Pega las coordenadas: -33.408639, -70.849947');
    return;
  }
  if (!results.length) {
    window.alert('No encontré ese lugar. Prueba con menos detalle (calle y comuna).');
    return;
  }
  placeSearchMarker(results[0].lat, results[0].lon);
  setAddress(results[0].title + (results[0].subtitle ? `, ${results[0].subtitle}` : ''));
}

// ------------------------------------------------------------ Biblioteca local

function refreshProjectList() {
  const select = $('listaProy');
  const projects = listProjects();
  const previous = select.value;
  select.innerHTML = '';

  if (!projects.length) {
    const option = document.createElement('option');
    option.value = '';
    option.textContent = '(aún no hay proyectos guardados)';
    select.appendChild(option);
    return;
  }

  projects.forEach((project) => {
    const option = document.createElement('option');
    option.value = project.id;
    option.textContent = `${project.nombre}  ·  ${project.fecha.slice(0, 10)}`;
    select.appendChild(option);
  });
  if (previous) select.value = previous;
}

// -------------------------------------------------------------------- Cableado

function updateDocCode() {
  const field = $('codigoDoc');
  if (!field.dataset.touched) {
    field.value = autoDocCode($('numLamina').value, $('versionDoc').value);
  }
}

function bindUi() {
  ['sitio', 'sede', 'direccion'].forEach((id) => {
    $(id).addEventListener('input', onCommit);
  });
  ['numLamina', 'versionDoc'].forEach((id) => {
    $(id).addEventListener('input', () => {
      updateDocCode();
      onCommit();
    });
  });
  $('codigoDoc').addEventListener('input', (event) => {
    event.target.dataset.touched = '1';
    onCommit();
  });

  $('btnIr').addEventListener('click', goToPlace);
  $('btnGoogle').addEventListener('click', () => {
    const query = searchMarker
      ? `${searchMarker.getLatLng().lat},${searchMarker.getLatLng().lng}`
      : $('goto').value.trim();
    if (!query) {
      window.alert('Escribe una dirección o busca un punto primero.');
      return;
    }
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      '_blank',
      'noopener',
    );
  });

  const gotoInput = $('goto');
  gotoInput.addEventListener('input', () => {
    const value = gotoInput.value.trim();
    clearTimeout(searchTimer);
    if (value.length < 3 || /^-?\d+\.\d/.test(value)) {
      closeSuggestions();
      return;
    }
    // El debounce alto es deliberado: Nominatim limita a 1 request por segundo.
    searchTimer = setTimeout(() => searchSuggestions(value), GEOCODER_DEBOUNCE_MS);
  });
  gotoInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      goToPlace();
    }
  });
  document.addEventListener('click', (event) => {
    if (!event.target.closest('#sugbox')) closeSuggestions();
  });

  $('bearing').addEventListener('input', (event) => setBearing(map, event.target.value));
  $('btnNorte').addEventListener('click', () => {
    setBearing(map, 0);
    $('bearing').value = 0;
  });

  $('btnCalles').addEventListener('click', () => {
    const enabled = basemaps.toggleLabels();
    $('btnCalles').textContent = `Calles y nombres: ${enabled ? 'ON' : 'OFF'}`;
  });

  $('baseProv').addEventListener('change', async (event) => {
    const requested = event.target.value;
    const applied = await basemaps.select(requested);
    const notice = $('baseAviso');
    if (applied !== requested) {
      // Ver docs/hallazgos.md: Stadia devuelve el rechazo como PNG válido, así
      // que sin este aviso el operador vería teselas de error sin explicación.
      event.target.value = applied;
      notice.textContent =
        'Stadia rechazó este dominio (autoriza por dominio, no por API key). Se usó Esri.';
      notice.classList.add('alerta');
    } else {
      notice.textContent = '';
      notice.classList.remove('alerta');
    }
  });

  $('radio').addEventListener('change', (event) => {
    radius = Number.parseFloat(event.target.value) || DEFAULT_RADIUS_M;
    deaLayer.setRadius(radius);
    onCommit();
  });

  $('btnDeaPropuesto').addEventListener('click', () => addDea('propuesto'));
  $('btnDeaAdquirido').addEventListener('click', () => addDea('adquirido'));
  $('btnEdificio').addEventListener('click', () => {
    buildingLayer.promptCreate();
    autosave.schedule();
  });

  $('btnPerimetro').addEventListener('click', () => {
    const drawing = perimeter.toggleDrawing();
    const button = $('btnPerimetro');
    button.textContent = drawing ? '✓ Cerrar perímetro' : 'Marcar perímetro';
    button.classList.toggle('activo', drawing);
  });
  $('btnDeshacer').addEventListener('click', () => perimeter.undoVertex());
  $('btnBorrarPerimetro').addEventListener('click', () => {
    perimeter.clear();
    $('btnPerimetro').textContent = 'Marcar perímetro';
    $('btnPerimetro').classList.remove('activo');
    autosave.schedule();
  });

  $('btnPreview').addEventListener('click', openSheetPreview);
  $('btnPng').addEventListener('click', () => exportSheet('png'));
  $('btnPdf').addEventListener('click', () => exportSheet('pdf'));

  $('btnLimpiar').addEventListener('click', () => {
    if (!window.confirm('¿Borrar todos los DEA y edificios del plano?')) return;
    deaLayer.clear();
    buildingLayer.clear();
    onCommit();
  });

  $('btnGuardar').addEventListener('click', () => {
    const suggested = `${metadata().codigoDoc} ${metadata().sitio}`.trim() || 'Proyecto';
    const name = window.prompt('Nombre del proyecto a guardar:', suggested);
    if (name === null) return;
    const id = name.trim();
    if (!id) return;
    if (hasProject(id) && !window.confirm(`Ya existe un proyecto "${id}". ¿Reemplazarlo?`)) return;
    if (!saveProject(id, serializeProject())) {
      window.alert('No se pudo guardar: almacenamiento lleno.');
      return;
    }
    refreshProjectList();
    $('listaProy').value = id;
    window.alert(`Guardado: ${id}\n\nLo abres desde la lista cuando quieras.`);
  });

  $('btnAbrir').addEventListener('click', () => {
    const id = $('listaProy').value;
    if (!id) {
      window.alert('No hay proyecto seleccionado en la lista.');
      return;
    }
    const data = loadProject(id);
    if (!data) {
      window.alert('Proyecto no encontrado.');
      return;
    }
    loadProjectData(data);
    window.alert(`Proyecto cargado: ${id}`);
  });

  $('btnEliminar').addEventListener('click', () => {
    const id = $('listaProy').value;
    if (!id) {
      window.alert('No hay proyecto seleccionado.');
      return;
    }
    if (!window.confirm(`¿Eliminar "${id}" de tu lista? (no afecta a los .json exportados)`)) return;
    deleteProject(id);
    refreshProjectList();
  });

  $('btnNuevo').addEventListener('click', () => {
    if (!window.confirm('¿Empezar un proyecto nuevo? Se borrará el trabajo actual guardado.')) {
      return;
    }
    autosave.clear();
    deaLayer.clear();
    buildingLayer.clear();
    perimeter.clear();
    ['sitio', 'sede', 'direccion'].forEach((id) => {
      $(id).value = '';
    });
    refresh();
  });

  $('btnExportar').addEventListener('click', () => {
    const meta = metadata();
    downloadJson(serializeProject(), exportFileName(meta.codigoDoc, meta.sitio));
  });

  $('btnImportar').addEventListener('click', () => $('fileProy').click());
  $('fileProy').addEventListener('change', (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        loadProjectData(JSON.parse(reader.result));
        window.alert(`Proyecto cargado: ${$('sitio').value || 'sin nombre'}`);
      } catch {
        window.alert('Archivo de proyecto no válido.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  });
}

function addDea(estado) {
  const center = map.getCenter();
  deaLayer.add(center.lat, center.lng, estado, 0);
  autosave.schedule();
}

// ------------------------------------------------------------------- Arranque

async function start() {
  logoImage = new Image();
  logoImage.src = logoUrl;

  bindUi();
  makeDraggable($('resumen'), map);

  $('baseProv').value = DEFAULT_BASEMAP;
  const applied = await basemaps.select(DEFAULT_BASEMAP);
  $('baseProv').value = applied;

  if (!supportsRotation(map)) {
    // Sin motor de rotación el control sobra; mejor esconderlo que dejarlo
    // moviéndose sin efecto.
    $('bearing').disabled = true;
    $('btnNorte').disabled = true;
  }

  const restored = autosave.restore();
  if (restored) loadProjectData(restored);

  updateDocCode();
  refreshProjectList();
  refresh();
}

start();
